import { LitElement, html, css } from 'lit';
import moment from 'moment-timezone';

import './neb-time-list';
import './neb-radio-button';
import './inputs/neb-select';
import { getAppointmentTypeLocationPublic } from '../../../neb-api-client/src/appointment-types';
import { getPublic } from '../../../neb-api-client/src/availability-api-client';
import { store } from '../../../neb-redux/neb-redux-store';
import { baseStyles } from '../../../neb-styles/neb-styles';
import {
  CSS_COLOR_HIGHLIGHT,
  CSS_FONT_WEIGHT_BOLD,
  CSS_SPACING,
} from '../../../neb-styles/neb-variables';
import { objToName } from '../../../neb-utils/formatters';

import { TYPE } from './neb-calendar-view';

export const ELEMENTS = {
  backToDateContainer: { id: 'container-back-to-date' },
  calendarView: { id: 'calendar-view' },
  providerDropdown: { id: 'provider-dropdown' },
  locationDropdown: { id: 'location-dropdown' },
  timeRangeDropdown: { id: 'time-range-dropdown' },
  serviceNameContainer: { id: 'service-name-container' },
  timeList: { id: 'time-list' },
  selectedDateContainer: { id: 'selected-date-container' },
  findByContainer: { id: 'find-by-containter' },
  radioDate: { id: 'radio-date' },
  radioTime: { id: 'radio-time' },
};

export const FIND_BY_TYPE = {
  DATE: 'date',
  TIME: 'time',
};

export const MOBILE_VIEW = {
  CALENDAR: 'calendar',
  TIMES: 'times',
};

const TEXT_BACK_TO_DATE = 'CHANGE DATE';

function createKronoStamp(idx) {
  const hour = Math.floor(idx / 12);
  const minute = (idx * 5) % 60;

  const padHour = hour < 10 ? `0${hour}` : hour;
  const padMinute = minute < 10 ? `0${minute}` : minute;

  return `${padHour}:${padMinute}`;
}

function formatDate(date, timezone) {
  return moment.tz(date, timezone).format('YYYY-MM-DD');
}

const today = () => moment();

class NebDateTimePage extends LitElement {
  static get properties() {
    return {
      __availability: Object,
      __availableTimes: Array,
      __displayPage: Object,
      __formattedSelectedDate: String,
      __locations: Array,
      __appointmentTypeLocation: Array,
      __verifiedLocations: Array,
      __providers: Array,
      __timeFrames: Array,
      __currentMobileView: String,

      __selectedDate: Object,
      __selectedLocation: Object,
      __selectedProvider: Object,
      __selectedTimeFrame: Object,
      __selectedFindBy: String,

      appointment: Object,
      layout: {
        type: String,
        reflect: true,
      },
    };
  }

  static get styles() {
    return [
      baseStyles,
      css`
        :host {
          display: flex;
        }

        :host([layout='small']) {
          flex-direction: column;
        }

        :host([layout='small']) > * {
          padding: 0 ${CSS_SPACING};
        }

        :host([layout='small']) > .container-calendar {
          padding: 0;
        }

        .service-name {
          font-size: 16px;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          height: 40px;
          display: flex;
          align-items: center;
        }

        .container {
          display: flex;
          flex-direction: column;
          flex: 1 0 0;
        }

        .container-calendar {
          display: flex;
          justify-content: center;
        }

        .container-dropdown {
          display: flex;
          flex-direction: column;
          justify-content: center;
          height: 80px;
        }

        .container-find-by {
          display: flex;
          align-items: center;
        }

        .container-find-by > span {
          font-size: 16px;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          color: ${CSS_COLOR_HIGHLIGHT};
        }

        .container-left {
          padding: ${CSS_SPACING} 10px ${CSS_SPACING} 0;
        }

        .container-right {
          padding: ${CSS_SPACING} 0 ${CSS_SPACING} 10px;
        }

        .dropdown {
          padding-top: 10px;
        }

        .selected-date {
          color: ${CSS_COLOR_HIGHLIGHT};
          font-size: 16px;
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
          margin: ${CSS_SPACING} 0;
        }

        .radio-button {
          --label-font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .spacer {
          margin: 8px 0 16px;
          height: 19px;
        }

        .container-back-to-date {
          align-items: center;
          cursor: pointer;
          display: flex;
          margin: 8px 0 16px;
          width: fit-content;
        }

        .container-back-to-date:hover {
          opacity: 0.5;
        }

        .back-label {
          text-decoration: underline;
          color: ${CSS_COLOR_HIGHLIGHT};
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .icon {
          transform: rotate(90deg);
          width: 16px;
          height: 16px;
          fill: ${CSS_COLOR_HIGHLIGHT};
          margin-right: 5px;
        }
      `,
    ];
  }

  constructor() {
    super();

    this.initState();
    this.initHandlers();
  }

  initState() {
    this.__availability = null;
    this.__selectedProvider = {};
    this.__selectedLocation = {};
    this.__selectedTimeFrame = {};
    this.__selectedFindBy = FIND_BY_TYPE.DATE;
    this.__currentMobileView = MOBILE_VIEW.CALENDAR;
    this.__providers = [];
    this.__locations = [];
    this.__appointmentTypeLocation = [];
    this.__verifiedLocations = [];
    this.__availableTimes = [];
    this.__timeFrames = [];

    this.appointment = {};

    this.setDisplayedMonth();

    this.onFormCompleted = () => {};
  }

  initHandlers() {
    this.__handlers = {
      timeSelected: time => {
        const locationId =
          this.__selectedLocation && this.__selectedLocation.id
            ? this.__selectedLocation.id
            : this.__locations[0].id;

        this.onFormCompleted({
          locationId,
          providerId: this.__selectedProvider.id,
          date: this.__selectedDate,
          time: time.value,
        });
      },
      isDateSelectable: date => {
        if (!this.__availability) return false;

        const formattedDate = formatDate(date, this.__timezone);

        return (
          this.__availability[formattedDate] &&
          this.__availability[formattedDate].length
        );
      },
      back: async () => {
        if (this.__currentMobileView === MOBILE_VIEW.TIMES) {
          this.__currentMobileView = MOBILE_VIEW.CALENDAR;

          await this.reloadAvailability(this.getDisplayedMonth());
        }
      },
      dateChanged: date => {
        if (this.layout === 'small') {
          this.__currentMobileView = MOBILE_VIEW.TIMES;
        }

        this.__selectedDate = date;

        this.__formattedSelectedDate = formatDate(
          this.__selectedDate,
          this.__timezone,
        );

        this.__availableTimes =
          this.__availability[this.__formattedSelectedDate];
      },
      monthChanged: async month => {
        await this.fetchAvailability(
          moment()
            .month(month.month - 1)
            .year(month.year),
        );

        this.__displayPage = month;
      },
      locationChanged: async ({ value }) => {
        if (value && value.id !== this.__selectedLocation.id) {
          this.__selectedLocation = value;

          await this.reloadAvailability(this.getDisplayedMonth());
        }
      },
      providerChanged: async item => {
        if (
          item &&
          item.value &&
          item.value.id !== this.__selectedProvider.id
        ) {
          this.__selectedProvider = item.value;

          this.__timeFrames = this.__allTimeRanges[this.__selectedProvider.id];
          this.__selectedTimeFrame = {};

          await this.reloadAvailability(this.getDisplayedMonth());
        }
      },
      timeFrameChanged: async item => {
        if (
          item &&
          item.value &&
          item.value.value !== this.__selectedTimeFrame.value
        ) {
          this.__selectedTimeFrame = item.value;

          await this.reloadAvailability(this.getDisplayedMonth());
        }
      },
      setFindBy: async value => {
        if (value === this.__selectedFindBy) return;

        this.__selectedFindBy = value;
        this.__selectedTimeFrame = {};

        await this.reloadAvailability(this.getDisplayedMonth());
      },
    };
  }

  loadDateAndTimes(date) {
    this.__selectedDate = date;

    this.__formattedSelectedDate = formatDate(
      this.__selectedDate,
      this.__timezone,
    );

    this.__availableTimes = this.__availability[this.__formattedSelectedDate];
  }

  async fetchAvailability(date) {
    const query = {
      apptTypeId: this.appointment.appointmentTypeId,
      start: date.startOf('month').format('YYYY-MM-DD'),
      end: date.endOf('month').format('YYYY-MM-DD'),
      providerId: this.__selectedProvider.id,
      locationId: this.__selectedLocation.id,
      timeBetween: this.__selectedTimeFrame.value,
      forOnlineBooking: true,
    };

    this.__availability = await getPublic(this.__shortName, query);
  }

  async reloadAvailability(date = moment.tz(this.__timezone)) {
    await this.fetchAvailability(date);

    const firstDate = this.setFirstDate();

    if (firstDate) {
      this.loadDateAndTimes(firstDate);
    } else {
      this.__availableTimes = [];
    }

    this.setDisplayedMonth(date);
  }

  async initializeAvailability() {
    let date = moment.tz(this.__timezone);

    await this.fetchAvailability(date);

    let firstDate = this.setFirstDate();

    if (firstDate) {
      this.loadDateAndTimes(firstDate);
    } else {
      date = date.add(1, 'months');

      await this.fetchAvailability(date);

      firstDate = this.setFirstDate();

      if (firstDate) {
        this.loadDateAndTimes(firstDate);
      } else {
        this.__selectedDate = null;
        this.__formattedSelectedDate = null;
        this.__availableTimes = [];
        date = date.subtract(1, 'months');
      }
    }

    this.setDisplayedMonth(date);
  }

  getDisplayedMonth() {
    return moment()
      .month(this.__displayPage.month - 1)
      .year(this.__displayPage.year);
  }

  setDisplayedMonth(date = today()) {
    this.__displayPage = { month: date.month() + 1, year: date.year() };
  }

  setFirstDate() {
    const first = Object.entries(this.__availability)
      .sort(([a, _], [b, __]) => a.localeCompare(b))
      .slice(1, -1)
      .find(([_, slots]) => slots.length);

    if (
      this.__availability[this.__formattedSelectedDate] &&
      this.__availability[this.__formattedSelectedDate].length
    ) {
      return this.__selectedDate;
    }

    if (this.appointment.start) {
      const date = formatDate(this.appointment.start, this.__timezone);

      if (this.__availability[date] && this.__availability[date].length) {
        return moment.tz(date, this.__timezone).add(12, 'hours').toDate();
      }
    }

    return first
      ? moment.tz(first[0], this.__timezone).add(12, 'hours').toDate()
      : null;
  }

  async filterLocationByAppointmentType() {
    this.__appointmentTypeLocation = await getAppointmentTypeLocationPublic(
      this.__shortName,
    );

    const serviceLocation = this.__appointmentTypeLocation.filter(
      item => item.appointmentTypeId === this.appointment.appointmentTypeId,
    );

    return this.__locations.filter(l =>
      serviceLocation.some(relation => relation.locationId === l.id),
    );
  }

  buildTimeRangesByProvider(providers, providerAvailabilities, apptTypeId) {
    const providerTimeFrames = providers.reduce((memo, provider) => {
      const providerHasAvailability = providerAvailabilities[provider.id];

      let openHours;

      if (providerHasAvailability) {
        openHours = Object.values(
          providerAvailabilities[provider.id].availability,
        )
          .filter(a => a.enabled)
          .map(a => a.periods)
          .flat()
          .filter(
            period =>
              period.allTypes || period.types.indexOf(apptTypeId) !== -1,
          )
          .map(filteredPeriod => {
            const hoursToReturn = [];

            for (
              let i = Math.floor(filteredPeriod.start);
              i < Math.ceil(filteredPeriod.end);
              i += 1
            ) {
              hoursToReturn.push(i);
            }

            return hoursToReturn;
          })
          .flat()
          .sort((a, b) => (a > b ? 1 : -1));
      }

      if (openHours && openHours.length > 0) {
        memo[provider.id] = [...new Set(openHours)].map(hour => ({
          duration: [hour, hour + 1],
          value: `${createKronoStamp(hour * 12)}-${
            hour === 23 ? '23:55' : createKronoStamp((hour + 1) * 12)
          }`,
          label: `${moment().hour(hour).minute(0).format('h:mm A')} - ${moment()
            .hour(hour + 1 === 24 ? 23 : hour + 1)
            .minute(hour + 1 === 24 ? 59 : 0)
            .format('h:mm A')}`,
        }));
      }

      return memo;
    }, {});

    return providerTimeFrames;
  }

  defaultSelectedValues() {
    this.__selectedLocation =
      this.__verifiedLocations.find(
        l => l.id === this.appointment.locationId,
      ) || this.__verifiedLocations[0];

    this.__selectedProvider =
      this.__providers.find(prov => prov.id === this.appointment.providerId) ||
      this.__providers[0];

    this.__timeFrames = this.__allTimeRanges[this.__selectedProvider.id];
  }

  async loadMetadata() {
    const state = store.getState();

    this.__shortName = state.practiceInfo.practiceInfoItems[0].shortName;
    this.__timezone = state.practiceInfo.practiceInfoItems[0].timezone;
    this.__locations = state.practiceInfo.practiceInfoItems[0].locations
      .filter(item => item.allowOnlineBooking)
      .map(item => ({
        ...item,
        label: item.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    this.__verifiedLocations = await this.filterLocationByAppointmentType();

    this.__allTimeRanges = this.buildTimeRangesByProvider(
      state.providers.item,
      state.providersAvailability.item,
      this.appointment.appointmentTypeId,
    );

    this.__providers = state.providers.item
      .filter(provider => this.__allTimeRanges[provider.id] && provider.active)
      .map(provider => ({
        ...provider,
        label: objToName(provider.name, { reverse: true }),
      }));

    this.defaultSelectedValues();
  }

  async updated(changedProps) {
    if (
      changedProps.has('appointment') &&
      this.appointment &&
      Object.keys(this.appointment).length
    ) {
      await this.loadMetadata();

      await this.initializeAvailability();
    }
  }

  __renderNavigation() {
    return this.__currentMobileView === MOBILE_VIEW.TIMES
      ? html`
          <div
            id="${ELEMENTS.backToDateContainer.id}"
            class="container-back-to-date"
            @click="${this.__handlers.back}"
          >
            <neb-icon
              id="neb-chevron-icon"
              class="icon"
              icon="neb:chevron"
            ></neb-icon>
            <span class="back-label">${TEXT_BACK_TO_DATE}</span>
          </div>
        `
      : html` <div class="spacer"></div> `;
  }

  __renderFindBy() {
    return html`
      <div id="${ELEMENTS.findByContainer.id}" class="container-find-by">
        <span>Find by</span>
        <neb-radio-button
          id="${ELEMENTS.radioDate.id}"
          class="radio-button"
          .onSelect="${this.__handlers.setFindBy}"
          .value="${'date'}"
          .label="${'Date'}"
          .checked="${this.__selectedFindBy === FIND_BY_TYPE.DATE}"
        >
        </neb-radio-button>
        <neb-radio-button
          id="${ELEMENTS.radioTime.id}"
          class="radio-button"
          .onSelect="${this.__handlers.setFindBy}"
          .value="${'time'}"
          .label="${'Time'}"
          .checked="${this.__selectedFindBy === FIND_BY_TYPE.TIME}"
        >
        </neb-radio-button>
      </div>
    `;
  }

  __renderTimeFrameDropdown() {
    if (this.__selectedFindBy === FIND_BY_TYPE.TIME) {
      return html`
        <div class="container-dropdown">
          <neb-select
            id="${ELEMENTS.timeRangeDropdown.id}"
            label="Time Frame"
            .items="${this.__timeFrames}"
            .value="${this.__selectedTimeFrame}"
            .onChange="${this.__handlers.timeFrameChanged}"
            .layout="${this.layout}"
          ></neb-select>
        </div>
      `;
    }

    if (this.layout === 'small') {
      return '';
    }

    return html` <div class="container-dropdown"></div> `;
  }

  __renderCalendar() {
    return html`
      <div class="container-calendar">
        <neb-calendar-view
          id="${ELEMENTS.calendarView.id}"
          .type="${TYPE.BUBBLES}"
          .isToolbarVisible="${false}"
          .selectedDate="${this.__selectedDate}"
          .isDateSelectable="${this.__handlers.isDateSelectable}"
          .onChange="${this.__handlers.dateChanged}"
          .onMonthChanged="${this.__handlers.monthChanged}"
          .displayPage="${this.__displayPage}"
        ></neb-calendar-view>
      </div>
    `;
  }

  __renderLocationDropdown() {
    return html`
      <div class="container-dropdown">
        <neb-select
          id="${ELEMENTS.locationDropdown.id}"
          label="Location"
          .items="${this.__verifiedLocations}"
          .value="${this.__selectedLocation}"
          .onChange="${this.__handlers.locationChanged}"
          .layout="${this.layout}"
          pinLabel
        ></neb-select>
      </div>
    `;
  }

  __renderProviderDropdown() {
    return html`
      <div class="container-dropdown">
        <neb-select
          id="${ELEMENTS.providerDropdown.id}"
          label="Provider"
          .items="${this.__providers}"
          .value="${this.__selectedProvider}"
          .onChange="${this.__handlers.providerChanged}"
          .layout="${this.layout}"
          pinLabel
        ></neb-select>
      </div>
    `;
  }

  __renderServiceName() {
    return html`
      <div id="${ELEMENTS.serviceNameContainer.id}" class="service-name">
        ${this.appointment.serviceName || this.appointment.appointmentTypeName}
        (${this.appointment.durationDisplay ||
        `${moment(this.appointment.end).diff(
          this.appointment.start,
          'minutes',
        )} Minutes`})
      </div>
    `;
  }

  __renderDateAndTimeList() {
    return html`
      <div id="${ELEMENTS.selectedDateContainer.id}" class="selected-date">
        ${this.__selectedDate
          ? moment
              .tz(this.__selectedDate, this.__timezone)
              .format('dddd, MMMM D, YYYY')
          : ''}
      </div>
      <neb-time-list
        id="${ELEMENTS.timeList.id}"
        .times="${this.__availableTimes}"
        .onTimeSelected="${this.__handlers.timeSelected}"
      ></neb-time-list>
    `;
  }

  __renderBig() {
    return html`
      <div class="container container-left">
        ${this.__renderFindBy()} ${this.__renderTimeFrameDropdown()}
        ${this.__renderCalendar()}
      </div>

      <div class="container container-right">
        ${this.__renderServiceName()} ${this.__renderLocationDropdown()}
        ${this.__renderProviderDropdown()} ${this.__renderDateAndTimeList()}
      </div>
    `;
  }

  __renderCalendarView() {
    return html`
      ${this.__renderNavigation()} ${this.__renderServiceName()}
      ${this.__renderFindBy()} ${this.__renderTimeFrameDropdown()}
      ${this.__renderLocationDropdown()} ${this.__renderProviderDropdown()}
      ${this.__renderCalendar()}
    `;
  }

  __renderTimeView() {
    return html`
      ${this.__renderNavigation()} ${this.__renderServiceName()}
      ${this.__renderDateAndTimeList()}
    `;
  }

  __renderSmall() {
    return this.__currentMobileView === MOBILE_VIEW.TIMES
      ? this.__renderTimeView()
      : this.__renderCalendarView();
  }

  render() {
    return this.layout === 'small' ? this.__renderSmall() : this.__renderBig();
  }
}

customElements.define('neb-date-time-page', NebDateTimePage);
